<template>
    <Modal :title="modalTitle()" modal-class="" :modal-name="modalName" size="xl" :backdrop-close="false" @close="sendCancelEvent">
        <div v-if="options.length > 0" class="premium-modal">
            <!--            <p class="premium-modal__description" v-html="$t('cart.premiumBox.info')"></p>-->

            <div class="premium-modal__product-list">
                <div
                    v-for="(option, index) in options"
                    :key="index"
                    class="premium-modal__product"
                    :class="{ 'premium-modal__product--active': productSelected === option.id && !benefitVariantSelected }"
                    @click="selectPremiumProduct(option.id)"
                >
                    <div class="premium-modal__product--check-wrapper">
                        <i class="check"></i>
                    </div>
                    <div>
                        <div class="premium-modal__product--header-wrapper">
                            <h5 class="mb-0">{{ $t('cart.premiumBox.premium') }}</h5>
                            <span class="premium-modal__product--badge-duration">{{ option.titleLength }}</span>
                        </div>

                        <div class="premium-modal__product--price-wrapper">
                            <span class="premium-modal__product--price notranslate" v-html="$formatCurrency(option.price)"></span>
                            <s v-if="option.rrp > 0 && option.rrp > option.price">{{ $formatCurrency(option.rrp) }}</s>
                            <span
                                class="premium-modal__product--price-per-month"
                                v-html="$formatCurrency(Math.round(option.pricePerMonth * 100) / 100) + '/' + $t('cart.premiumBox.month')"
                            >
                            </span>
                        </div>

                        <span v-if="option.badge" class="premium-modal__product--badge d-inline-block">{{ option.badge }}</span>
                    </div>
                </div>
            </div>

            <div v-if="!userIsLoggedIn" class="premium-modal__login">
                <p class="text-center">{{ $t('cart.premiumBox.loginMessage') }}</p>
                <ElementsCartLoginForm :dark-mode="true" :google-login-link="$t('cart.login.googleLinkPilulkaPro')" />
                <div class="text-center">
                    <p class="mt-5">{{ $t('cart.premiumBox.registrationMessage') }}</p>
                    <nuxt-link :to="$t('url.user.registration')" class="btn btn-primary btn-primary-arrow">{{
                        $t('registration.buttonTitle')
                    }}</nuxt-link>
                </div>
            </div>
            <LayoutUserPilulkaPremiumPayment
                v-else-if="activeOption"
                :key="activeOption.id"
                :product-id="activeOption.id"
                :price="activeOption.price"
                :show-other-payments="activeOption.id === yearOption.id"
                :user-credit-cards="userCreditCards"
                :initial-voucher-code="voucherCode"
                @add-to-basket-clicked="addPremiumProductToCart(activeOption.id)"
            />

            <p class="premium-modal__disclaimer mt-3" v-html="$t('cart.premiumBox.modalDisclaimer')"></p>
        </div>
    </Modal>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import PremiumProduct from '~/models/Product/PremiumProduct'
import Product from '~/models/Product/Product'
import DataLayer from '~/extensions/DataLayer'
import GtmEventActions from '~/value/GtmEventActions'
import { useBasketStore } from '~/stores/basket'
import UserCreditCard from '~/models/Api/Cart/UserCreditCard'

const i18n = useI18n()

const basketStore = useBasketStore()
const userIsLoggedIn = computed(() => basketStore.authUser !== null)

const modalName = 'cart-premium-modal'
const options = ref<PremiumProduct[]>([])
const products = ref<Product[]>([])
const productSelected = ref<number>(0)
const PREMIUM_MODAL_EVENT = 'premium_modal'
const PREMIUM_MODAL_EVENT_INFO_TYPE = 'info'
const benefitVariantSelected = ref(false)
const voucherCode = ref<string | undefined>(undefined)

const yearOption = computed(() => {
    return options.value.filter((option) => option.validTill === '+1 year')[0]
})

const activeOption = computed(() => {
    return options.value.filter((option) => option.id === productSelected.value)[0]
})

const modalTitle = () => {
    let title = `<div class="d-flex"><h3 class="fs-18 mb-0 mt-0 pr-2">${i18n.t('cart.premiumBox.title')}</h3>`
    title += `<img src="/nuxt-assets/design2025/pilulka-pro-logo-green.svg" width="110" class="ml-2" /></div>`

    return title
}

const selectPremiumProduct = (id: number): void => {
    productSelected.value = id
    benefitVariantSelected.value = false
}

const closeModal = (shouldSendCancelEvent: boolean) => {
    useNuxtApp().$bus.$emit('hideModal')
    if (shouldSendCancelEvent) {
        sendCancelEvent()
    }
}

const removePremiumProductsFromCart = (): void => {
    products.value.forEach((product) => {
        addProductToBasket({
            product,
            productId: product.id,
            amount: 0,
            showPreBasket: false,
        })
    })
}

const addPremiumProductToCart = (id: number): void => {
    removePremiumProductsFromCart()
    const product = products.value.find((product) => product.id === id)
    if (product) {
        addProductToBasket({
            product,
            productId: product.id,
            amount: 1,
            showPreBasket: false,
        })
        const premiumElements = document.getElementsByClassName('premium-modal__product')
        DataLayer.push({
            event: PREMIUM_MODAL_EVENT,
            status: GtmEventActions.SELECT,
            variant: premiumElements[0].classList.contains('premium-modal__product--active') ? 'year' : '3_month',
            popup_type: PREMIUM_MODAL_EVENT_INFO_TYPE,
        })
    }
    closeModal(false)
}

const accessToken = useAccessTokenCookie().value

const userCreditCards = ref<UserCreditCard[]>([])

onMounted(() => {
    useNuxtApp().$bus.$off('showCartPremiumModal')
    useNuxtApp().$bus.$on(
        'showCartPremiumModal',
        async ({
            premiumOptions,
            premiumProducts,
            selectedProductId,
            voucherCode: voucherCodeParam,
        }: {
            premiumOptions: PremiumProduct[]
            premiumProducts: Product[]
            selectedProductId: number | null
            voucherCode?: string
        }) => {
            options.value = premiumOptions
            products.value = premiumProducts
            productSelected.value = selectedProductId ?? 0
            voucherCode.value = voucherCodeParam

            userCreditCards.value = []
            if (accessToken) {
                const response = await useNuxtApp().$repository.listUserCreditCards(accessToken || '')
                userCreditCards.value = response.response?.cards || []
            }

            useNuxtApp().$bus.$emit('showModal', modalName)

            DataLayer.push({
                event: PREMIUM_MODAL_EVENT,
                status: GtmEventActions.OPEN,
                popup_type: PREMIUM_MODAL_EVENT_INFO_TYPE,
            })
        }
    )
})

const sendCancelEvent = () => {
    DataLayer.push({
        event: PREMIUM_MODAL_EVENT,
        status: GtmEventActions.CANCEL,
        popup_type: PREMIUM_MODAL_EVENT_INFO_TYPE,
    })
}

onUnmounted(() => {
    useNuxtApp().$bus.$off('showCartPremiumModal')
})
</script>

<style scoped lang="scss">
.premium-modal {
    color: #333;

    &__login {
        width: 80%;
        margin: 40px auto;
    }

    &__description {
        font-size: 14px;
        line-height: 1.5;
        margin-bottom: 20px;
    }

    &__disclaimer {
        color: #696969;
        font-size: 13px;
    }

    &__average-saving {
        background: #63874d1a;
        border-radius: 10px;
        text-align: center;
        padding: 15px;
    }

    &__btn-nope {
        font-size: 14px;
        font-weight: bold;
        color: $color-cta !important;
    }

    &__product-list {
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: 1rem;
        @include screen-md {
            flex-wrap: nowrap;
        }
    }

    &__product {
        background: #fff;
        box-shadow: 0 0 19px -8px rgba(0, 0, 0, 0.33);
        border-radius: 10px;
        color: #000;
        padding: 20px 10px;
        display: flex;
        align-items: center;
        width: 100%;
        cursor: pointer;
        border: 2px solid transparent;
        margin-top: 10px;

        @include screen-md {
            margin-top: 0;
            max-width: 33%;
        }

        &--price {
            color: $color-cta;
            font-weight: bold;
            font-size: 16px;
        }

        &--price-per-month {
            font-size: 16px;
            color: #333;
        }

        &--check-wrapper {
            width: 25px;
            margin-right: 15px;

            .check {
                width: 24px;
                height: 24px;
                display: block;

                border: 1px solid #b7b7b7;
                border-radius: 12px;
            }
        }

        &--header-wrapper {
            display: flex;
            flex-wrap: wrap;
            column-gap: 1rem;
            row-gap: 0.25rem;
            margin-bottom: 0.5rem;
            flex-direction: column;

            @include screen-sm {
                flex-direction: row;
            }
        }

        &--price-wrapper {
            display: flex;
            flex-wrap: wrap;
            gap: 0.4rem 1rem;
            margin-bottom: 0.5rem;
        }

        &--active {
            border: 2px solid $color-secondary-dark-green-1;

            .check {
                border-color: $color-secondary-dark-green-1;
                background: $color-secondary-dark-green-1 url('/nuxt-assets/images/icons/checkbox-checked.svg') 4px 5px no-repeat;
                background-size: 13px;
                color: #fff;
            }
        }

        &--badge {
            color: $color-cta;
            background: #e6007e0d;
            border-radius: 20px;
            padding: 8px 15px;
            font-weight: bold;
        }

        &--badge-duration {
            color: #c97b19;
            background: #fff4e0;
            font-size: 12px;
            border-radius: 20px;
            padding: 2px 10px;
            font-weight: bold;
            text-align: center;
        }
    }

    &__deliveries {
        background: #fff;
        box-shadow: 0 0 19px -8px rgba(0, 0, 0, 0.33);
        border-radius: 10px;
        color: #000;
        padding: 20px 10px;
        display: flex;
        align-items: center;
        width: 100%;
        border: 2px solid transparent;
    }

    &__delivery {
        border-bottom: 1px solid #f6f6f6;
        color: #333;
        padding: 8px 0;
        align-items: center;

        &--icon {
            width: 34px;
            height: 34px;
            display: flex;
            justify-content: center;
            align-items: center;
            background: #f6f6f6;
            border-radius: 10px;

            img {
                width: 18px;
            }
        }

        &--title {
            flex-grow: 1;
            line-height: 115%;
        }

        &--price-new {
            color: $color-green-dark;
            background: lighten($color-green-dark, 50%);
            border-radius: 20px;
            padding: 8px 15px;
            font-weight: bold;
        }
    }
}
</style>
